// Facebook SDK optimization for better caching and performance
'use client';

import { useEffect, useRef } from 'react';

interface FacebookSDKConfig {
  appId?: string;
  version?: string;
  lazy?: boolean;
}

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit: () => void;
  }
}

export function useFacebookSDK(config: FacebookSDKConfig = {}) {
  const isLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  const loadFacebookSDK = () => {
    // Prevent duplicate loading
    if (isLoadedRef.current || isLoadingRef.current) {
      return Promise.resolve(window.FB);
    }

    // Check if already loaded
    if (window.FB) {
      isLoadedRef.current = true;
      return Promise.resolve(window.FB);
    }

    isLoadingRef.current = true;

    return new Promise((resolve, reject) => {
      // Set up FB init callback before loading script
      window.fbAsyncInit = () => {
        window.FB.init({
          appId: config.appId ?? process.env.NEXT_PUBLIC_FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: config.version ?? 'v18.0',
        });

        isLoadedRef.current = true;
        isLoadingRef.current = false;
        resolve(window.FB);
      };

      // Load Facebook SDK script with optimized caching
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;

      // Use versioned URL for better caching
      script.src = `https://connect.facebook.net/en_US/sdk.js#version=${config.version ?? 'v18.0'}`;

      // Add cache-friendly attributes
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        // FB init will be called automatically via fbAsyncInit
      };

      script.onerror = () => {
        isLoadingRef.current = false;
        reject(new Error('Failed to load Facebook SDK'));
      };

      // Insert script
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    });
  };

  useEffect(() => {
    // Only load if not in lazy mode
    if (!config.lazy) {
      loadFacebookSDK().catch(console.error);
    }
  }, [config.lazy, config.appId, config.version]);

  return {
    loadFacebookSDK,
    isLoaded: isLoadedRef.current,
    isLoading: isLoadingRef.current,
  };
}

// Preload Facebook resources for better caching
export function preloadFacebookResources() {
  if (typeof window === 'undefined') return;

  // Preconnect to Facebook domains
  const domains = [
    'https://connect.facebook.net',
    'https://www.facebook.com',
    'https://static.xx.fbcdn.net',
  ];

  domains.forEach((domain) => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });

  // DNS prefetch for Facebook tracking pixels
  const dnsPrefetchDomains = [
    'https://www.facebook.com',
    'https://connect.facebook.net',
  ];

  dnsPrefetchDomains.forEach((domain) => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = domain;
    document.head.appendChild(link);
  });
}
